import { motion } from "framer-motion";
import { useTranslation } from "../i18n/LanguageContext";

const cardContainer = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
};
const cardItem = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function HowWeWorkSection() {
  const { t } = useTranslation();
  const w = t.howWeWork;
  if (!w) return null;

  return (
    <>
      {/* MAIN SECTION — BLOCK A (text + image) + Cards */}
      <section
        id="how-we-work"
        className="py-24 md:py-32 bg-[#0A0A0B]"
        data-testid="how-we-work-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* BLOCK A — Two columns: text (55%) + image (45%) */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="md:col-span-7"
            >
              <div className="flex items-center gap-4 mb-6">
                <motion.span
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="inline-block w-10 h-px bg-[#C9A96E]"
                  style={{ transformOrigin: "left center" }}
                />
                <span className="fwd-eyebrow" data-testid="how-eyebrow">
                  {w.eyebrow}
                </span>
              </div>

              <h2 className="fwd-heading mb-8 whitespace-pre-line" data-testid="how-headline">
                {w.headline}
              </h2>

              <p className="fwd-body mb-6" data-testid="how-paragraph-1">
                {w.paragraph1}
              </p>

              <p
                className="fwd-body"
                style={{ color: "#F5F1E8", fontWeight: 600 }}
                data-testid="how-paragraph-2"
              >
                {w.paragraph2}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="md:col-span-5"
              data-testid="how-image-wrapper"
            >
              <div
                className="overflow-hidden"
                style={{ border: "1px solid #2A2A2E", borderRadius: "2px" }}
              >
                <img
                  src="/How_we_work.jpg"
                  alt="A boardroom at night overlooking the city"
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>

          {/* BLOCK B — Four cards (2x2) */}
          <motion.div
            variants={cardContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 gap-[2px] bg-[#0A0A0B]"
            data-testid="how-cards-grid"
          >
            {w.cards.map((card) => (
              <motion.div
                key={card.number}
                variants={cardItem}
                className="bg-[#141417] border border-[#2A2A2E] border-l-2 border-l-[#C9A96E] p-8 fwd-card-hover group"
                style={{ borderRadius: "2px" }}
                data-testid={`how-card-${card.number}`}
              >
                <motion.span
                  initial={{ opacity: 0.3 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                  className="block mb-4 fwd-number"
                  data-testid={`how-card-number-${card.number}`}
                  style={{
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 600,
                    fontSize: "16px",
                    letterSpacing: "0.22em",
                    color: "#C9A96E",
                  }}
                >
                  {card.number}
                </motion.span>
                <h3
                  className="mb-4"
                  style={{
                    fontFamily: "Cormorant Garamond, serif",
                    fontWeight: 500,
                    fontSize: "28px",
                    lineHeight: 1.2,
                    color: "#F5F1E8",
                  }}
                >
                  {card.title}
                </h3>
                <p className="fwd-body" style={{ fontSize: "16px" }}>
                  {card.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CLOSING BAND — distinct background, centered statement */}
      <section
        className="w-full"
        style={{ background: "#0E0E10" }}
        data-testid="how-closing-band"
      >
        <div
          className="mx-auto px-6 md:px-12 text-center"
          style={{ maxWidth: "720px", paddingTop: "100px", paddingBottom: "100px" }}
        >
          <ClosingBand w={w} />
        </div>
      </section>
    </>
  );
}

function ClosingBand({ w }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: "easeOut" }}
    >
      {/* Gold line above headline */}
      <motion.span
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="block mx-auto mb-10"
        style={{
          width: "40px",
          height: "1px",
          background: "#C9A96E",
          transformOrigin: "center",
        }}
      />

      <h3 className="fwd-heading mb-8 whitespace-pre-line" data-testid="how-closing-1">
        {w.closing1}
      </h3>

      <p className="fwd-body mb-12" data-testid="how-closing-2">
        {w.closing2}
      </p>

      <p
        className="whitespace-pre-line mb-10"
        style={{
          fontFamily: "Cormorant Garamond, serif",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "36px",
          lineHeight: 1.3,
          color: "#C9A96E",
        }}
        data-testid="how-closing-3"
      >
        {w.closing3}
      </p>

      {/* Gold line below italic lines */}
      <motion.span
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
        className="block mx-auto"
        style={{
          width: "40px",
          height: "1px",
          background: "#C9A96E",
          transformOrigin: "center",
        }}
      />
    </motion.div>
  );
}
